import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import type { TenantAccessSummary, TenantMembership, UserRole } from '../lib/db/types';

interface TenantContextType {
  user: User | null;
  tenantId: string | null;
  role: UserRole | null;
  roles: UserRole[];
  tenants: TenantAccessSummary[];
  activeTenant: TenantAccessSummary | null;
  activeMembership: TenantMembership | null;
  billingStatus: TenantAccessSummary['billingStatus'];
  onboardingStatus: TenantAccessSummary['onboardingStatus'];
  loading: boolean;
  switchTenant: (tenantId: string) => void;
  refreshTenants: () => Promise<void>;
  refreshClaims: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
  user: null,
  tenantId: null,
  role: null,
  roles: [],
  tenants: [],
  activeTenant: null,
  activeMembership: null,
  billingStatus: null,
  onboardingStatus: null,
  loading: true,
  switchTenant: () => {},
  refreshTenants: async () => {},
  refreshClaims: async () => {},
});

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

const activeTenantStorageKey = (uid: string) => `crm.activeTenantId.${uid}`;

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenants, setTenants] = useState<TenantAccessSummary[]>([]);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const activeTenant = useMemo(
    () => tenants.find((tenant) => tenant.tenantId === activeTenantId) || null,
    [activeTenantId, tenants]
  );

  const refreshTenants = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setTenants([]);
      setActiveTenantId(null);
      return;
    }

    const getMyTenantContext = httpsCallable(functions, 'getMyTenantContext');
    const result = await getMyTenantContext();
    const data = result.data as { primaryTenantId?: string | null; tenants?: TenantAccessSummary[] };
    const nextTenants = data.tenants || [];
    setTenants(nextTenants);

    const storedTenantId = localStorage.getItem(activeTenantStorageKey(currentUser.uid));
    const nextActiveTenant =
      nextTenants.find((tenant) => tenant.tenantId === storedTenantId) ||
      nextTenants.find((tenant) => tenant.tenantId === data.primaryTenantId) ||
      nextTenants[0] ||
      null;

    setActiveTenantId(nextActiveTenant?.tenantId || null);
    if (nextActiveTenant) {
      localStorage.setItem(activeTenantStorageKey(currentUser.uid), nextActiveTenant.tenantId);
    }
  };

  const switchTenant = (tenantId: string) => {
    const found = tenants.find((tenant) => tenant.tenantId === tenantId);
    if (!found || !user) return;
    localStorage.setItem(activeTenantStorageKey(user.uid), tenantId);
    setActiveTenantId(tenantId);
  };

  // Backward-compatible name for pages that used to refresh custom claims.
  const refreshClaims = refreshTenants;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(true);

      try {
        if (currentUser) {
          await refreshTenants();
        } else {
          setTenants([]);
          setActiveTenantId(null);
        }
      } catch (error) {
        console.error('Error loading tenants:', error);
        setTenants([]);
        setActiveTenantId(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const value: TenantContextType = {
    user,
    tenantId: activeTenant?.tenantId || null,
    role: activeTenant?.role || null,
    roles: activeTenant?.roles || [],
    tenants,
    activeTenant,
    activeMembership: activeTenant?.membership || null,
    billingStatus: activeTenant?.billingStatus || null,
    onboardingStatus: activeTenant?.onboardingStatus || null,
    loading,
    switchTenant,
    refreshTenants,
    refreshClaims,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
